import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Table, Alert, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";
import DocumentUpload from "../common/DocumentUpload";

export default function CreateDispatchModal({ show, onHide, salesOrderId, onDispatchCreated, orderItems }) {
  const [dispatchType, setDispatchType] = useState("Partial");
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverContact, setDriverContact] = useState("");
  const [transporter, setTransporter] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [documents, setDocuments] = useState([]);

  // New state for transporters, vehicles, and drivers
  const [transporters, setTransporters] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  const [selectedTransporter, setSelectedTransporter] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");

  const [loadingTransporters, setLoadingTransporters] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Vehicle weight fields
  const [vehicleWeightBeforeLoad, setVehicleWeightBeforeLoad] = useState("");
  const [weightSlipBefore, setWeightSlipBefore] = useState(null);

  const [selectedItems, setSelectedItems] = useState([]);
  const [productStockMap, setProductStockMap] = useState({}); // Store stock info per product
  const [loading, setLoading] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);

  const fetchTransporters = useCallback(async () => {
    setLoadingTransporters(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/transporters`);
      setTransporters(res.data || []);
    } catch (err) {
      console.error("Error fetching transporters:", err);
      toast.error("Failed to fetch transporters");
    } finally {
      setLoadingTransporters(false);
    }
  }, []);

  const fetchVehicles = async (transporterId) => {
    if (!transporterId) return;
    setLoadingVehicles(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/vehicles/by-transporter/${transporterId}`);
      setVehicles(res.data || []);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
      toast.error("Failed to fetch vehicles for the selected transporter");
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchDrivers = async (transporterId) => {
    if (!transporterId) return;
    setLoadingDrivers(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/drivers/by-transporter/${transporterId}`);
      setDrivers(res.data || []);
    } catch (err) {
      console.error("Error fetching drivers:", err);
      toast.error("Failed to fetch drivers for the selected transporter");
    } finally {
      setLoadingDrivers(false);
    }
  };

  const fetchProductStock = async (productId, packingMaterialId) => {
    try {
      // const res = await axios.get(`${config.API_BASE_URL}/inventory/product/${productId}/stock`);
      const res = await axios.get(`${config.API_BASE_URL}/inventory/product/${productId}/packing-material/${packingMaterialId}/stock`);
      return res.data || { data: [] };
    } catch (err) {
      console.error(`Error fetching stock for product ${productId}:`, err);
      return { data: [] };
    }
  };

  const initializeItems = useCallback(async () => {
    setLoadingStock(true);
    try {
      // Get already dispatched quantities
      const dispatchRes = await axios.get(`${config.API_BASE_URL}/dispatches`, {
        params: { salesOrderId }
      });
      const dispatches = dispatchRes.data || [];

      const dispatchedMap = {};
      dispatches.forEach(dispatch => {
        if (dispatch.items && Array.isArray(dispatch.items)) {
          dispatch.items.forEach(item => {
            const key = item.salesOrderItem?._id || item.salesOrderItem;
            if (!dispatchedMap[key]) {
              dispatchedMap[key] = 0;
            }
            dispatchedMap[key] += item.quantityDispatched || 0;
          });
        }
      });

      // Initialize items with remaining quantities
      const itemsWithRemaining = (orderItems || [])
        .map(item => ({
          salesOrderItemId: item._id,
          product: item.product,
          packingBagType: item.packingBagType,
          productName: item.product?.productName || 'Unknown Product',
          orderedQuantity: item.quantity || 0,
          alreadyDispatched: dispatchedMap[item._id] || 0,
          remainingQty: (item.quantity || 0) - (dispatchedMap[item._id] || 0),
          unit: item.unit || 'units',
          quantityToDispatch: 0,
          warehouseLocationId: "",
          quality: "good",
          packingMaterialId: item.packingBagType?._id || null,      // Inner Packing Material ID from SO Item
          quantityPerBag: item.packingBagType?.itemWiseStuffing?.find(
            stuffing => stuffing.product?._id === item.product?._id
          )?.quantity || null,         // Qty per Inner Bag from SO Item's packing material's itemWiseStuffing for this product
          numberOfBags: null,           // Number of Inner Bags
          outerPackingMaterialId: null, // Outer Packing Material ID
          outerNumberOfBags: null,      // Number of Outer Bags
          bagsPerOuterBag: null,        // Bags per Outer Bag
          availableBags: null,          // Available Inner Bags from stock
          availableOuterBags: null,     // Available Outer Bags from stock
          rowId: `${item._id}-${Date.now()}-${Math.random()}`
        }))
        .filter(item => item.remainingQty > 0);

      setSelectedItems(itemsWithRemaining);

      // Fetch stock for all products
      const stockMap = {};
      for (const item of itemsWithRemaining) {
        if (item.product && item.product._id && item.packingBagType && item.packingBagType._id) {
          const stockData = await fetchProductStock(item.product._id, item.packingBagType._id);
          stockMap[`${item.product._id}_${item.packingBagType._id}`] = stockData;
        }
      }
      setProductStockMap(stockMap);

    } catch (err) {
      console.error("Error initializing items:", err);
      toast.error("Error loading order items");
    } finally {
      setLoadingStock(false);
    }
  },[orderItems, salesOrderId]);
  
  useEffect(() => {
    if (show && salesOrderId) {
      // Reset and reinitialize every time modal is shown
      setSelectedItems([]);
      setProductStockMap({});
      initializeItems();
      fetchTransporters();
    }
  }, [show, salesOrderId, initializeItems, fetchTransporters]);

  const handleQuantityChange = (index, value) => {
    const updated = [...selectedItems];
    let qty = parseFloat(value) || 0;

    // Calculate dynamic remaining based on all rows for same product
    const salesOrderItemId = updated[index].salesOrderItemId;
    const orderItem = orderItems.find(item => item._id === salesOrderItemId);

    // Total from DB dispatch history
    const dbDispatched = updated[index].alreadyDispatched;

    // Total being dispatched in current form (excluding current row)
    const currentFormTotal = updated
      .filter((item, i) => item.salesOrderItemId === salesOrderItemId && i !== index)
      .reduce((sum, item) => sum + (item.quantityToDispatch || 0), 0);

    const maxOrderQty = (orderItem?.quantity || 0) - dbDispatched - currentFormTotal;

    // Auto-correct: Check against order remaining quantity
    if (qty > maxOrderQty) {
      qty = maxOrderQty;
      toast.info(`Quantity auto-corrected to ${maxOrderQty.toFixed(2)} ${updated[index].unit} (order remaining)`);
    }

    // Retrieve selectedLocationStock within this function's scope
    const availableLocationsForProduct = getAvailableLocationsForProduct(
      updated[index].product?._id,
      updated[index].packingBagType?._id
    );
    const selectedLocationStock = availableLocationsForProduct.find(
      loc => loc.locationId === updated[index].warehouseLocationId &&
             loc.quality === updated[index].quality &&
             loc.packingMaterialId === updated[index].packingMaterialId &&
             loc.outerPackingMaterialId === updated[index].outerPackingMaterialId
    );

    // Auto-correct: Check against available stock at selected location
    if (selectedLocationStock && qty > selectedLocationStock.totalQuantity) { // Use the more specific selectedLocationStock
        qty = selectedLocationStock.totalQuantity;
        toast.info(`Quantity auto-corrected to ${selectedLocationStock.totalQuantity} ${updated[index].unit} (available at this warehouse)`);
    }

    updated[index].quantityToDispatch = qty;

    // --- Dynamic allocation for pickedInwardEntries based on qty ---
    // This logic assumes we fulfill the dispatched quantity from the earliest inward entries first.
    let remainingQtyToAllocate = qty;
    let remainingInnerBagsToAllocate = updated[index].numberOfBags || 0; // Use calculated or manual inner bags
    let remainingOuterBagsToAllocate = updated[index].outerNumberOfBags || 0; // Use calculated or manual outer bags

    const newPickedInwardEntries = [];
    if (selectedLocationStock && selectedLocationStock.inwardEntries) {
      // Sort inward entries if necessary (e.g., by date) - assuming they are in a reasonable order
      const sortedInwardEntries = [...selectedLocationStock.inwardEntries]; // Make a copy

      for (const entry of sortedInwardEntries) {
        if (remainingQtyToAllocate <= 0 && remainingInnerBagsToAllocate <= 0 && remainingOuterBagsToAllocate <= 0) break;

        const pickQty = Math.min(remainingQtyToAllocate, entry.quantity);
        const pickInnerBags = Math.min(remainingInnerBagsToAllocate, entry.numberOfBags || 0);
        const pickOuterBags = Math.min(remainingOuterBagsToAllocate, entry.outerNumberOfBags || 0);

        if (pickQty > 0 || pickInnerBags > 0 || pickOuterBags > 0) {
          newPickedInwardEntries.push({
            inwardUnloadingEntryId: entry._id,
            quantityPicked: pickQty,
            numberOfBagsPicked: pickInnerBags,
            outerNumberOfBagsPicked: pickOuterBags,
          });
          remainingQtyToAllocate -= pickQty;
          remainingInnerBagsToAllocate -= pickInnerBags;
          remainingOuterBagsToAllocate -= pickOuterBags;
        }
      }
    }
    updated[index].pickedInwardEntries = newPickedInwardEntries;
    // --- End dynamic allocation ---


    // Auto-calculate inner bags if packing material is present
    if (updated[index].packingMaterialId && updated[index].quantityPerBag > 0) {
      let calculatedInnerBags = Math.ceil(qty / updated[index].quantityPerBag);
      if (updated[index].availableBags !== null && calculatedInnerBags > updated[index].availableBags) {
        calculatedInnerBags = updated[index].availableBags;
        toast.info(`Inner bags auto-corrected to ${calculatedInnerBags} (available at this stock location)`);
      }
      updated[index].numberOfBags = calculatedInnerBags;

      // Auto-calculate outer bags if outer packing material is present
      if (updated[index].outerPackingMaterialId && updated[index].bagsPerOuterBag > 0) {
        let calculatedOuterBags = Math.ceil(calculatedInnerBags / updated[index].bagsPerOuterBag);
        if (updated[index].availableOuterBags !== null && calculatedOuterBags > updated[index].availableOuterBags) {
          calculatedOuterBags = updated[index].availableOuterBags;
          toast.info(`Outer bags auto-corrected to ${calculatedOuterBags} (available at this stock location)`);
        }
        updated[index].outerNumberOfBags = calculatedOuterBags;
      }
    }


    // Update remaining qty for all rows of same product
    updated.forEach((item, i) => {
      if (item.salesOrderItemId === salesOrderItemId) {
        const otherRowsTotal = updated
          .filter((otherItem, otherIndex) =>
            otherItem.salesOrderItemId === salesOrderItemId &&
            otherIndex !== i
          )
          .reduce((sum, otherItem) => sum + (otherItem.quantityToDispatch || 0), 0);

        item.remainingQty = (orderItem?.quantity || 0) - item.alreadyDispatched - otherRowsTotal;
      }
    });

    setSelectedItems(updated);
  };

  const handleLocationChange = (index, locationQualityKey) => {
    const updated = [...selectedItems];

    if (locationQualityKey) {
      // Key format: "locationId-QUALITY-quality-PACK-packingMaterialId-outerPackingMaterialId"
      const mainParts = locationQualityKey.split('-PACK-');
      const locationAndQualityPart = mainParts[0];
      const packingDetailsKey = mainParts[1];

      const qualityParts = locationAndQualityPart.split('-QUALITY-');
      const locationId = qualityParts[0];
      const quality = qualityParts[1];

      // Find the detailed location info from the availableLocations list
      const availableLocations = getAvailableLocationsForProduct(
        updated[index].product?._id,
        updated[index].packingBagType?._id // This refers to the SalesOrderItem's default packing
      );
      
      // Need to find the exact stock entry that matches the selected location, quality, AND packing config
      const selectedLocationStock = availableLocations.find(
        loc => loc.locationId === locationId && 
               loc.quality === quality &&
               `${loc.packingMaterialId}-${loc.outerPackingMaterialId}` === packingDetailsKey
      );

      updated[index].warehouseLocationId = locationId;
      updated[index].quality = quality || 'good';
      updated[index].packingMaterialId = selectedLocationStock?.packingMaterialId || null;
      updated[index].packingMaterialName = selectedLocationStock?.packingMaterialName || null;
      updated[index].quantityPerBag = selectedLocationStock?.quantityPerBag || null;
      updated[index].availableBags = selectedLocationStock?.availableBags || null;
      updated[index].outerPackingMaterialId = selectedLocationStock?.outerPackingMaterialId || null;
      updated[index].outerPackingMaterialName = selectedLocationStock?.outerPackingMaterialName || null;
      updated[index].availableOuterBags = selectedLocationStock?.availableOuterBags || null;
      updated[index].bagsPerOuterBag = selectedLocationStock?.bagsPerOuterBag || null;
      
      // Populate pickedInwardEntries based on the selected stock's inwardEntries
      updated[index].pickedInwardEntries = selectedLocationStock?.inwardEntries?.map(entry => ({
        inwardUnloadingEntryId: entry._id,
        quantityPicked: entry.quantity, // Initially pick the full available quantity from this specific entry
        numberOfBagsPicked: entry.numberOfBags,
        outerNumberOfBagsPicked: entry.outerNumberOfBags,
      })) || [];

      // Recalculate numberOfBags and outerNumberOfBags based on new packing config and current quantityToDispatch
      const currentQty = updated[index].quantityToDispatch;
      if (updated[index].packingMaterialId && updated[index].quantityPerBag > 0) {
        let calculatedInnerBags = Math.ceil(currentQty / updated[index].quantityPerBag);
        if (updated[index].availableBags !== null && calculatedInnerBags > updated[index].availableBags) {
          calculatedInnerBags = updated[index].availableBags;
          // No toast here as it's not a user-initiated quantity change
        }
        updated[index].numberOfBags = calculatedInnerBags;

        if (updated[index].outerPackingMaterialId && updated[index].bagsPerOuterBag > 0) {
          let calculatedOuterBags = Math.ceil(calculatedInnerBags / updated[index].bagsPerOuterBag);
          if (updated[index].availableOuterBags !== null && calculatedOuterBags > updated[index].availableOuterBags) {
            calculatedOuterBags = updated[index].availableOuterBags;
            // No toast here
          }
          updated[index].outerNumberOfBags = calculatedOuterBags;
        } else {
          updated[index].outerNumberOfBags = null; // No outer packing material
        }
      } else {
        updated[index].numberOfBags = null; // No packing material or qty per bag
        updated[index].outerNumberOfBags = null;
      }

    } else { // When no location is selected (resetting)
      updated[index].warehouseLocationId = "";
      updated[index].quality = "good";
      updated[index].packingMaterialId = null;
      updated[index].packingMaterialName = null;
      updated[index].quantityPerBag = null;
      updated[index].numberOfBags = null;
      updated[index].availableBags = null;
      updated[index].outerPackingMaterialId = null;
      updated[index].outerPackingMaterialName = null;
      updated[index].outerNumberOfBags = null;
      updated[index].availableOuterBags = null;
      updated[index].bagsPerOuterBag = null;
    }

    setSelectedItems(updated); // Update state once at the end
  };

  const handleBagChange = (index, value) => {
    const updated = [...selectedItems];
    let bags = parseInt(value) || 0;

    // Validate against available bags
    if (updated[index].availableBags !== null && bags > updated[index].availableBags) {
      bags = updated[index].availableBags;
      toast.info(`Inner bags auto-corrected to ${bags} (available at this stock location)`);
    }

    updated[index].numberOfBags = bags;
    setSelectedItems(updated);
  };

  const handleOuterBagChange = (index, value) => {
    const updated = [...selectedItems];
    let outerBags = parseInt(value) || 0;

    // Validate against available outer bags
    if (updated[index].availableOuterBags !== null && outerBags > updated[index].availableOuterBags) {
      outerBags = updated[index].availableOuterBags;
      toast.info(`Outer bags auto-corrected to ${outerBags} (available at this stock location)`);
    }

    updated[index].outerNumberOfBags = outerBags;
    setSelectedItems(updated);
  };

  const handleRemoveItem = (index) => {
    const updated = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(updated);
  };

  const handleAddDocument = () => {
    setDocuments([...documents, { documentType: '', file: null, fileName: '' }]);
  };

  const handleRemoveDocument = (index) => {
    const updated = documents.filter((_, i) => i !== index);
    setDocuments(updated);
  };

  const handleTransporterChange = (e) => {
    const transporterId = e.target.value;
    setSelectedTransporter(transporterId);
    setSelectedVehicle("");
    setSelectedDriver("");
    setVehicleNumber("");
    setDriverName("");
    setDriverContact("");
    setVehicles([]);
    setDrivers([]);
    if (transporterId) {
      fetchVehicles(transporterId);
      fetchDrivers(transporterId);
    }
  };

  const handleVehicleChange = (e) => {
    const vehicleId = e.target.value;
    setSelectedVehicle(vehicleId);
    const vehicle = vehicles.find(v => v._id === vehicleId);
    if (vehicle) {
      setVehicleNumber(vehicle.vehicleNumber);
    } else {
      setVehicleNumber("");
    }
  };

  const handleDriverChange = (e) => {
    const driverId = e.target.value;
    setSelectedDriver(driverId);
    const driver = drivers.find(d => d._id === driverId);
    if (driver) {
      setDriverName(driver.name);
      setDriverContact(driver.phone);
      if (driver.vehicleId) {
        const vehicle = vehicles.find(v => v._id === driver.vehicleId);
        if(vehicle) {
          setSelectedVehicle(driver.vehicleId);
          setVehicleNumber(vehicle.vehicleNumber);
        }
      }
    } else {
      setDriverName("");
      setDriverContact("");
    }
  };

  const handleDocumentChange = (index, data) => {
    const updated = [...documents];
    updated[index] = data;
    setDocuments(updated);
  };

  const handleAddItemRow = (salesOrderItemId) => {
    // Find the original order item
    const orderItem = orderItems.find(item => item._id === salesOrderItemId);
    if (!orderItem) return;

    // Calculate total already dispatched (including current form)
    const totalAlreadyDispatched = selectedItems
      .filter(item => item.salesOrderItemId === salesOrderItemId)
      .reduce((sum, item) => sum + (item.quantityToDispatch || 0), 0);

    // Get dispatched from DB
    const dbDispatched = selectedItems.find(item => item.salesOrderItemId === salesOrderItemId)?.alreadyDispatched || 0;

    const remainingQty = (orderItem.quantity || 0) - dbDispatched - totalAlreadyDispatched;

    if (remainingQty <= 0) {
      toast.warning("No remaining quantity to dispatch for this product");
      return;
    }

    // Add new row for same product
    const newRow = {
      salesOrderItemId: orderItem._id,
      product: orderItem.product,
      packingBagType: orderItem.packingBagType,
      productName: orderItem.product?.productName || 'Unknown Product',
      orderedQuantity: orderItem.quantity || 0,
      alreadyDispatched: dbDispatched,
      remainingQty: remainingQty,
      unit: orderItem.unit || 'units',
      quantityToDispatch: 0,
      warehouseLocationId: "",
      quality: "good",
      rowId: `${orderItem._id}-${Date.now()}-${Math.random()}`
    };

    setSelectedItems([...selectedItems, newRow]);
    toast.success(`Added another location row for ${orderItem.product?.productName}`);
  };

  // Get available warehouses with stock for a specific product
  const getAvailableLocationsForProduct = (productId, packingMaterialId) => {
    const stockData = productStockMap[`${productId}_${packingMaterialId}`];
    if (!stockData || !stockData.data || stockData.data.length === 0) {
      return [];
    }

    return stockData.data
      .filter(stock => stock.warehouse && stock.availableStock > 0)
      .map(stock => ({
        locationId: stock.warehouse._id,
        locationLabel: stock.warehouse.name,
        quality: "good", // Assuming 'good' for now as quality is not deeply tracked here
        totalQuantity: stock.availableStock,
        unit: stock.unit,
        packingMaterialId: stock.packingMaterial?._id || null,
        packingMaterialName: stock.packingMaterial?.name || null,
        quantityPerBag: stock.quantityPerBag || null,
        availableBags: stock.availableBags || null,
        outerPackingMaterialId: stock.outerPackingMaterial?._id || null,
        outerPackingMaterialName: stock.outerPackingMaterial?.name || null,
        availableOuterBags: stock.availableOuterBags || null,
        bagsPerOuterBag: stock.bagsPerOuterBag || null, // Not directly from stock, but from InwardUnloading
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to dispatch");
      return;
    }

    const invalidItems = selectedItems.filter(
      item => !item.quantityToDispatch || item.quantityToDispatch <= 0
    );

    if (invalidItems.length > 0) {
      toast.error("Please enter valid quantities for all items");
      return;
    }

    const itemsWithoutLocation = selectedItems.filter(
      item => !item.warehouseLocationId
    );

    if (itemsWithoutLocation.length > 0) {
      toast.error("Please select warehouse location for all items");
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('salesOrderId', salesOrderId);
      formData.append('dispatchType', dispatchType);
      formData.append('dispatchDate', dispatchDate);
      formData.append('transporterId', selectedTransporter || '');
      formData.append('vehicleId', selectedVehicle || '');
      formData.append('driverId', selectedDriver || '');
      formData.append('vehicleNumber', vehicleNumber || '');
      formData.append('driverName', driverName || '');
      formData.append('driverContact', driverContact || '');
      formData.append('transporter', transporters.find(t => t._id === selectedTransporter)?.name || transporter);
      formData.append('expectedDeliveryDate', expectedDeliveryDate || '');
      formData.append('remarks', remarks || '');
      formData.append('vehicleWeightBeforeLoad', vehicleWeightBeforeLoad || '');

      // Add weight slip file if uploaded
      if (weightSlipBefore) {
        formData.append('weightSlipBefore', weightSlipBefore);
      }

      // Add additional documents
      documents.forEach((doc, index) => {
        if (doc.file) {
          formData.append(`documents[${index}][documentType]`, doc.documentType);
          formData.append(`documents[${index}][fileName]`, doc.documentType === 'Other' ? doc.fileName : doc.documentType);
          formData.append('documents', doc.file);
        }
      });

      // Add items
      selectedItems.forEach((item, index) => {
        formData.append(`items[${index}][salesOrderItemId]`, item.salesOrderItemId);
        formData.append(`items[${index}][product]`, item.product._id);
        formData.append(`items[${index}][quantityDispatched]`, item.quantityToDispatch);
        formData.append(`items[${index}][unit]`, item.unit);
        formData.append(`items[${index}][warehouseLocationId]`, item.warehouseLocationId);
        formData.append(`items[${index}][quality]`, item.quality);
        // Packing details
        formData.append(`items[${index}][packingMaterialId]`, item.packingMaterialId || '');
        formData.append(`items[${index}][quantityPerBag]`, item.quantityPerBag || '');
        formData.append(`items[${index}][numberOfBags]`, item.numberOfBags || '');
        formData.append(`items[${index}][outerPackingMaterialId]`, item.outerPackingMaterialId || '');
        formData.append(`items[${index}][outerNumberOfBags]`, item.outerNumberOfBags || '');
        formData.append(`items[${index}][bagsPerOuterBag]`, item.bagsPerOuterBag || '');

        // Also add pickedInwardEntries if available for precise stock deduction
        if (item.pickedInwardEntries && item.pickedInwardEntries.length > 0) {
          item.pickedInwardEntries.forEach((entry, entryIndex) => {
            formData.append(`items[${index}][pickedInwardEntries][${entryIndex}][inwardUnloadingEntryId]`, entry.inwardUnloadingEntryId);
            formData.append(`items[${index}][pickedInwardEntries][${entryIndex}][quantityPicked]`, entry.quantityPicked);
            formData.append(`items[${index}][pickedInwardEntries][${entryIndex}][numberOfBagsPicked]`, entry.numberOfBagsPicked || '');
            formData.append(`items[${index}][pickedInwardEntries][${entryIndex}][outerNumberOfBagsPicked]`, entry.outerNumberOfBagsPicked || ''); // NEW
          });
        }
      });

      const res = await axios.post(`${config.API_BASE_URL}/dispatches`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Dispatch note ${res.data.dispatchNumber} created successfully`);
      onDispatchCreated();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creating dispatch note");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDispatchType("Partial");
    setDispatchDate(new Date().toISOString().split('T')[0]);
    setVehicleNumber("");
    setDriverName("");
    setDriverContact("");
    setTransporter("");
    setExpectedDeliveryDate("");
    setRemarks("");
    setVehicleWeightBeforeLoad("");
    setWeightSlipBefore(null);
    setSelectedItems([]);
    setDocuments([]);
    onHide();
  };

  const totalQuantityToDispatch = selectedItems.reduce(
    (sum, item) => sum + (item.quantityToDispatch || 0),
    0
  );

  return (
    <Modal show={show} onHide={handleClose} fullscreen="xl-down" size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create Dispatch Note</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Dispatch Type</Form.Label>
                <Form.Select
                  value={dispatchType}
                  onChange={(e) => setDispatchType(e.target.value)}
                  required
                >
                  <option value="Full">Full Dispatch</option>
                  <option value="Partial">Partial Dispatch</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Dispatch Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dispatchDate}
                  onChange={(e) => setDispatchDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Expected Delivery Date</Form.Label>
                <Form.Control
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3 mb-3">Transport Information</h6>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Transporter</Form.Label>
                <div className="d-flex">
                  <Form.Select
                    value={selectedTransporter}
                    onChange={handleTransporterChange}
                    disabled={loadingTransporters}
                  >
                    <option value="">{loadingTransporters ? 'Loading...' : '-- Select Transporter --'}</option>
                    {transporters.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                  </Form.Select>
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Vehicle Number</Form.Label>
                <div className="d-flex">
                  <Form.Select
                    value={selectedVehicle}
                    onChange={handleVehicleChange}
                    disabled={!selectedTransporter || loadingVehicles}
                  >
                    <option value="">{loadingVehicles ? 'Loading...' : '-- Select Vehicle --'}</option>
                    {vehicles.map(v => <option key={v._id} value={v._id}>{v.vehicleNumber}</option>)}
                  </Form.Select>
                </div>
                <Form.Control
                  type="text"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  placeholder="Or enter manually"
                  className="mt-1"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Driver</Form.Label>
                <div className="d-flex">
                  <Form.Select
                    value={selectedDriver}
                    onChange={handleDriverChange}
                    disabled={!selectedTransporter || loadingDrivers}
                  >
                    <option value="">{loadingDrivers ? 'Loading...' : '-- Select Driver --'}</option>
                    {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                  </Form.Select>
                </div>
                 <Form.Control
                  type="text"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  placeholder="Or enter manually"
                  className="mt-1"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Driver Contact</Form.Label>
                <Form.Control
                  type="tel"
                  value={driverContact}
                  onChange={(e) => setDriverContact(e.target.value)}
                  placeholder="Driver contact"
                />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3 mb-3">Vehicle Weight Information</h6>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tare Vehicle Weight (Empty Truck) <small className="text-muted">(kg)</small></Form.Label>
                <Form.Control
                  type="number"
                  value={vehicleWeightBeforeLoad}
                  onChange={(e) => setVehicleWeightBeforeLoad(e.target.value)}
                  placeholder="e.g., 2500"
                  min="0"
                  step="0.01"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Weight Slip (Before Load)</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => setWeightSlipBefore(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <Form.Text className="text-muted">
                  Upload weight slip document (PDF, JPG, PNG)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <h6 className="mt-3 mb-3">Additional Documents</h6>
          {documents.map((doc, index) => (
            <DocumentUpload
              key={index}
              index={index}
              onDocumentChange={handleDocumentChange}
              onRemoveDocument={handleRemoveDocument}
            />
          ))}
          <Button variant="secondary" size="sm" onClick={handleAddDocument}>
            + Add Document
          </Button>

          <h6 className="mt-4 mb-3">Items to Dispatch</h6>

          {loadingStock ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p>Loading stock information...</p>
            </div>
          ) : selectedItems.length === 0 ? (
            <Alert variant="info">
              No items available for dispatch or all items have been dispatched.
            </Alert>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table bordered hover responsive size="sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "12%" }}>Product</th>
                    <th style={{ width: "8%" }}>Ordered</th>
                    <th style={{ width: "8%" }}>Dispatched</th>
                    <th style={{ width: "8%" }}>Remaining</th>
                    <th style={{ width: "10%" }}>Qty to Dispatch *</th>
                    <th style={{ width: "10%" }}>Inner Bags *</th>
                    <th style={{ width: "10%" }}>Outer Bags</th>
                    <th style={{ width: "24%" }}>Location & Packing (Available) *</th>
                    <th style={{ width: "10%" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItems.map((item, index) => {
                    const availableLocations = getAvailableLocationsForProduct(
                      item.product?._id,
                      item.packingBagType?._id
                    );
                    // const availableLocations = getAvailableLocationsForProduct(item.product?._id);
                    const selectedPackingKey = `${item.packingMaterialId}-${item.outerPackingMaterialId}`;
                    const selectedKey = item.warehouseLocationId
                      ? `${item.warehouseLocationId}-QUALITY-${item.quality}-PACK-${selectedPackingKey}`
                      : "";

                    // Find available quantity at selected location
                    const selectedLocation = availableLocations.find(
                      loc => loc.locationId === item.warehouseLocationId &&
                        loc.quality === item.quality
                    );

                    return (
                      <tr key={item.rowId}>
                        <td>{item.productName}</td>
                        <td>{item.orderedQuantity} {item.unit}</td>
                        <td>{item.alreadyDispatched} {item.unit}</td>
                        <td>
                          <strong className="text-primary">{item.remainingQty.toFixed(2)} {item.unit}</strong>
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max={item.remainingQty}
                            step="0.01"
                            value={item.quantityToDispatch}
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                            required
                          />
                          {selectedLocation && (
                            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                              Max: {selectedLocation.totalQuantity} {selectedLocation.unit}
                            </Form.Text>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max={item.availableBags}
                            value={item.numberOfBags}
                            onChange={(e) => handleBagChange(index, e.target.value)}
                            required={!!item.packingMaterialId}
                            disabled={!item.packingMaterialId}
                          />
                          {item.packingMaterialId && (
                            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {item.packingMaterialName} Max: {item.availableBags} bags
                            </Form.Text>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            min="0"
                            max={item.availableOuterBags}
                            value={item.outerNumberOfBags}
                            onChange={(e) => handleOuterBagChange(index, e.target.value)}
                            required={!!item.outerPackingMaterialId}
                            disabled={!item.outerPackingMaterialId}
                          />
                          {item.outerPackingMaterialId && (
                            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {item.outerPackingMaterialName} Max: {item.availableOuterBags} outer bags
                            </Form.Text>
                          )}
                        </td>
                        <td>
                          {availableLocations.length === 0 ? (
                            <Alert variant="warning" className="mb-0 py-1 px-2" style={{ fontSize: '0.85rem' }}>
                              ⚠️ No stock available
                            </Alert>
                          ) : (
                            <Form.Select
                              size="sm"
                              value={selectedKey}
                              onChange={(e) => handleLocationChange(index, e.target.value)}
                              required
                            >
                              <option value="">-- Select Location & Packing --</option>
                              {availableLocations.map(loc => {
                                // Construct a unique key that includes packing material configuration
                                const packingKey = `${loc.packingMaterialId}-${loc.outerPackingMaterialId}`;
                                const key = `${loc.locationId}-QUALITY-${loc.quality}-PACK-${packingKey}`;
                                
                                let label = `${loc.locationLabel} | ${loc.quality}`;
                                if (loc.packingMaterialName) {
                                  label += ` | ${loc.packingMaterialName} (${loc.availableBags} bags)`;
                                } else {
                                  label += ` | Unpacked`;
                                }
                                if (loc.outerPackingMaterialName) {
                                  label += ` in ${loc.outerPackingMaterialName} (${loc.availableOuterBags} outer bags)`;
                                }
                                label += ` (Available: ${loc.totalQuantity} ${loc.unit})`;

                                return (
                                  <option key={key} value={key}>
                                    {label}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          )}
                        </td>

                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleAddItemRow(item.salesOrderItemId)}
                              title="Add another location for this product"
                            >
                              +
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                              title="Remove this row"
                            >
                              ✕
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="table-secondary">
                    <td colSpan="4" className="text-end"><strong>Total to Dispatch:</strong></td>
                    <td colSpan="4">
                      <strong>{totalQuantityToDispatch.toFixed(2)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any additional notes..."
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || selectedItems.length === 0}
          >
            {loading ? "Creating..." : "Create Dispatch Note"}
          </Button>
        </Modal.Footer>
      </Form>


    </Modal>
  );
}
